import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { AccordionDetails, AccordionSummary, Box, IconButton, Stack, Typography } from "@mui/material";
import AttachmentOutlinedIcon from '@mui/icons-material/AttachmentOutlined';
import MuiAccordion from "@mui/material/Accordion";
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FiSend } from "react-icons/fi";
import { RiDeleteBin6Line } from "react-icons/ri";
import moment from "moment";
import BaseImage from "../BaseImage";
import BaseButton from "../BaseButton";
import BaseTextareaAutosize from "../BaseTextareaAutosize";
import ProviderNoteItemSkeleton from "../../components-skeleton/ProviderNoteItemSkeleton"
import { openModal } from "../../store";
import { useMutation, useLazyQuery } from "@apollo/client";
import { GetProviderNotes, CreateProviderNote, DeleteProviderNote,
  VerifyUpload, CreateProviderNoteUploadAttachment
 } from "../../apollo-client/api-query";
import { showErrorToast } from "../../utils/toast";
import AttachedFile from "../AttachedFile";
import ChatRecentButton from "../ChatRecentButton";

const PatientNotes = ({ sx }) => {
  const dispatch = useDispatch()
  const { id } = useParams()
  const auth = useSelector(state => state.auth)
  const loadingAuth = useSelector(state => state.loadingAuth)
  const scrollContainerRef = useRef(null);

  const [getProviderNotes, { loading: loadingFetch }] = useLazyQuery(GetProviderNotes, {
    fetchPolicy: 'no-cache'
  });
  const [createProviderNote] = useMutation(CreateProviderNote)
  const [deleteProviderNote, { loading: loadingDelete }] = useMutation(DeleteProviderNote)
  const [createProviderNoteUploadAttachment] = useMutation(CreateProviderNoteUploadAttachment)
  const [isNoteCreating, setIsNoteCreating] = useState(false);
  const [verifyUpload] = useMutation(VerifyUpload)
  const [expanded, setExpanded] = useState(true)
  const [note, setNote] = useState('')
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [notes, setNotes] = useState([])
  const [error, setError] = useState('')
  const [attachedFiles, setAttachedFiles] = useState([]); // State to manage attached files
  const [showScrollToBottom , setShowScrollToBottom ] = useState(false);

  const fetchData = useCallback(async (first) => {
    if (!loadingFetch) {
      const result = await getProviderNotes({
        variables: {
          first,
          deleted: false,
          memberId: id,
        }
      })
  
      setHasMore(result?.data?.providerNotes?.pageInfo?.hasNextPage);
      if (result?.data?.providerNotes?.edges && result?.data?.providerNotes?.edges.length > 0) {
        setNotes(result?.data?.providerNotes?.edges);
        setPage((prevPage) => prevPage + 1);
      }
    }
  },[getProviderNotes, id, loadingFetch])
  
  useEffect(() => {
    fetchData(10)
  }, [])

  const handleChangeInput = (e) => {
    setError('')
    const value = e.target.value

    if (value.length <= 1000) {
      setNote(e.target.value)
    } else {
      setError('Maximum message length is 1000')
    }
  }
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
  
    // Filter for allowed file types: PDF, JPEG, JPG, PNG
    const allowedFiles = files.filter(file => 
      file.type === "application/pdf" ||
      file.type === "image/jpeg" ||
      file.type === "image/jpg" ||
      file.type === "image/png"
    );
  
    // Check if any disallowed files were selected
    if (allowedFiles.length !== files.length) {
      showErrorToast({ message: "Only PDF, JPEG, JPG, and PNG files are allowed." });
    }
  
    setAttachedFiles([...attachedFiles, ...allowedFiles]);
  };
  

  const handleRemoveFile = (index) => {
    setAttachedFiles(attachedFiles.filter((_, i) => i !== index));
  };

  const handleClickAddNote = async () => {
    if (note) {
      setIsNoteCreating(true);
      let isNewNoteCreated = false;
      try {
        const { data: { providerNoteCreate: createdNote } } = await createProviderNote({
          variables: {
            providerNoteInput: {
              memberId: id,
              body: note
            }
          }
        })
        isNewNoteCreated = true;
        if(attachedFiles.length > 0) {
          for (const file of attachedFiles) {
            try {
              const { data: { providerNoteUploadAttachment: uploadedFile } } = await createProviderNoteUploadAttachment({
                variables: {
                  providerNoteId: createdNote.id,
                  uploadInput: {
                    filename: file?.name,
                    mimeType: file?.type,
                    size: file?.size
                  }
                }
              })
              let headersArray = uploadedFile.headers.map((({ key, value }) => ({ [key]: value }))),
              headersObj = Object.assign({}, ...headersArray)
    
              const headers = {
                'X-Requested-With': '*',
                'Access-Control-Allow-Headers': 'Content-Type,X-Amz-Date,Authorization,X-Api-Key,x-requested-with',
                'Access-Control-Allow-Origin': '*',
                ...headersObj
              }

              await axios({
                method: 'PUT',
                url: uploadedFile.url,
                data: file,
                headers
              });
            
              await verifyUpload({
                variables: {
                  uploadId: uploadedFile.id
                }
              });
            } catch(e) {
            }
          }
        }
      } catch {

      }
      if(isNewNoteCreated) {
        if (hasMore) {
          fetchData(page * 10)
        } else {
          setHasMore(true)
          fetchData((page - 1) * 10 + 1)
        }
        const container = scrollContainerRef.current;
        container.scrollTop = container.scrollHeight;
        setNote('')
        setError('')
        setAttachedFiles([]);
      }
      setIsNoteCreating(false);
    } else {
      setError('Required provider note.')
    }
  }

  const removeNote = async (item) => {
    await deleteProviderNote({
      variables: {
        providerNoteId: item?.node?.id
      }
    }).catch(error => {
      showErrorToast({
        message: "Cannot delete someone else's note. Contact help@descendantsdna.com"
      })
    })
    setNotes(notes.filter(note => note?.node?.id !== item?.node?.id))
  }

  const handleClickRemoveNote = (item) => {
    dispatch(
      openModal({
        currentModal: 'CONFIRM_MODAL',
        modalProps: {
          title: '',
          description: 'Are you sure you want to delete this?',
          confirmText: 'Remove',
          loadingConfirm: loadingDelete,
          onConfirm: () => removeNote(item)
        }
    }))
  }

  // Handle Scroll Event within the container
  const handleScroll = useCallback(() => {
    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 50 && hasMore) {
      fetchData(page * 10);
    }
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50;
    setShowScrollToBottom(!isAtBottom);
  }, [hasMore, fetchData, page]);

  useEffect(() => {
    handleScroll();
  }, [notes])

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
        container.addEventListener('scroll', handleScroll);
    }

    return () => {
        if (container) {
            container.removeEventListener('scroll', handleScroll);
        }
    };
  }, [hasMore, handleScroll]);

  const scrollToBottom = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.style.scrollBehavior = "smooth"; // Enable smooth scrolling
      container.scrollTop = container.scrollHeight;
      setShowScrollToBottom(false);
      setHasMore(false);
  
      // Reset scroll behavior to auto after smooth scroll completes
      setTimeout(() => {
        container.style.scrollBehavior = "auto";
      }, 300); // Adjust this duration if needed
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [notes])
  
  return (
    <Box sx={sx}>
      <Accordion
        sx={{
          zIndex: 0,
          width: '100%',
          // marginBottom: "15px",
          backgroundColor: "primary.lightGray",
          borderRadius: "8px",
          padding: '16px',
        }}
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "primary.darkGray" }} />}
          aria-controls="panel1a-content"
          id="panel1a-header"
          sx={{
            padding: 0,
            minHeight: 0,
            alignItems: 'flex-start',

            '.MuiAccordionSummary-content': {
              margin: 0
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="h4" color="primary.black">Patient Notes</Typography>
            <Typography variant="body" color="primary.darkGray">
              Any notes here will appear on this patient's portal home screen.
            </Typography>            
          </Box>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            px: 0,
            pb: 0
          }}
        >
          <Box sx={{
            position: 'relative'
          }}>
            <Stack
              ref={scrollContainerRef}
              direction={'column'}
              spacing={'8px'}
              sx={{
                mt: '4px',
                pt: '16px',
                borderTop: '1px solid',
                borderColor: 'primary.mediumGray',
                maxHeight: '228px',
                overflowY: 'auto',
                pr: '4px'
              }}
              className="notification-dropdown-scroll"
            >
              {notes.map(((item) => (
                <Box
                  key={item?.node?.id}
                  sx={{
                    bgcolor: 'white',
                    borderRadius: '16px',
                    padding: '8px',
                    display: 'flex',
                  }}
                >
                  <BaseImage
                    src={item?.node?.staff?.avatarUrl}
                    sx={{
                      padding: '4px',
                      minWidth: '36px',
                      width: '36px',
                      height: '36px',
                    }}
                    sximage={{
                      borderRadius: '50%',
                      objectFit: 'cover'
                    }}
                  />
                  <Box
                    sx={{
                      width: '100%',
                      ml: '8px',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box>
                        <Typography variant="h4" color="primary.black">{item?.node?.staff?.name}</Typography>
                        <Typography variant="subText" color="primary.darkGray">{moment(item?.node?.createdAt).calendar()}</Typography>
                      </Box>
                      <Box
                        sx={{
                          cursor: 'pointer'
                        }}
                        onClick={() => handleClickRemoveNote(item)}
                      >
                        <RiDeleteBin6Line color='#0E0F10' size={24} />
                      </Box>
                    </Box>
                    <Box sx={{
                      paddingTop: '8px',
                      display: item?.node?.attachments?.length > 0 ? 'flex' : 'none',
                      flexWrap: 'wrap',
                      gap: '14px',
                      marginBottom: '8px'
                    }}>
                      {item?.node?.attachments?.map((file, index) => (
                        <AttachedFile 
                          key={index}
                          file={file} 
                          onDownload={() => {}} 
                        />
                      ))}
                    </Box>
                    <Box>
                      <Typography
                        variant="subText"
                        color="primary.darkGray"
                        sx={{
                          mt: '8px',
                          wordBreak: "break-word"
                        }}
                      >
                        {item?.node?.body}
                      </Typography>  
                    </Box>
                  </Box>
                </Box>
              )))}
              {(loadingFetch || loadingAuth) && <ProviderNoteItemSkeleton />}
            </Stack>
            {showScrollToBottom &&
                <ChatRecentButton 
                  sx={{
                    position: 'absolute',
                    bottom: '-5px',
                    left: '50%',
                    transform: 'translateX(-50%)'
                  }}
                  dir={false}
                  onClick={scrollToBottom}
                />
            }
          </Box>
          <Box
            sx={{
              mt: '16px',
              display: 'flex',
            }}
          >
            <BaseImage
              src={auth?.avatarUrl || '//ssl.gstatic.com/accounts/ui/avatar_2x.png'}
              sx={{
                padding: '4px',
                minWidth: '40px',
                width: '40px',
                height: '40px',
              }}
              sximage={{
                objectFit: 'cover',
                borderRadius: '50%',
              }}
            />
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                background: '#FFFFFF',
                border: '1px solid #ABB7C0',
                borderRadius: '8px',
                overflow: 'hidden'
              }}
            >
              <Box sx={{
                paddingTop: '8px',
                paddingLeft: '12px',
                display: attachedFiles.length > 0 ? 'flex' : 'none',
                justifyContent: 'flex-start',
                gap: '14px',
                marginBottom: '6px',
                flexWrap: 'wrap',
              }}>
                {attachedFiles.map((file, index) => (
                  <AttachedFile 
                    key={index}
                    file={file} 
                    onRemove={() => handleRemoveFile(index)} 
                  />
                ))}
              </Box>
              <Box
                sx={{
                  position: 'relative'
                }}
              >
                <input
                  type="file"
                  accept="application/pdf, image/jpeg, image/jpg, image/png"
                  multiple
                  style={{ display: 'none' }}
                  id="file-input-patientnote"
                  onChange={handleFileChange}
                />
                <label htmlFor="file-input-patientnote">
                  <IconButton 
                    component="span" 
                    disableRipple 
                    sx={{
                      position: 'absolute',
                      right: '117px',
                      top: '8px',
                      rotate: '-45deg',
                      color: '#000'
                    }}
                  >
                    <AttachmentOutlinedIcon/>
                  </IconButton>
                </label>
                <BaseTextareaAutosize
                  placeholder='Add provider note'
                  inputStyles={{
                    background: 'white',
                    color: '#5B87EF',
                    width: '100%',
                    fontSize: '12px',
                    paddingRight: '160px',
                    border: 'none',
                    borderRadius: 'unset'
                  }}
                  hasMaxLengthLabel={false}
                  maxLength={1000}
                  value={note}
                  onChange={handleChangeInput}
                />
                <BaseButton
                  height={'36px'}
                  buttonType={'primary'}
                  loading={loadingFetch || isNoteCreating}
                  disabled={!note && attachedFiles.length === 0}
                  sx={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    maxWidth: '102px',
                  }}
                  onClick={handleClickAddNote}
                >
                  <Typography variant="body" color='white' mr='8px'>
                    Send
                  </Typography>
                  <FiSend size={24} color='white' />
                </BaseButton>
              </Box>
              {error && (
                <Typography variant="subText" color='primary.red'>
                  {error}
                </Typography>
              )}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: 'none',
  
  '&:not(:last-child)': {
    borderBottom: 0,
    // marginBottom: '14px',
  },
  '&:before': {
    display: 'none',
  },
}));

export default PatientNotes